import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameStore } from '../../stores/gameStore';
import { useConversationStore } from '../../stores/conversationStore';

export const QuickActions: React.FC = () => {
  const navigate = useNavigate();
  const { selectedGame } = useGameStore();
  const { createNewConversation } = useConversationStore();

  const handleStartChat = async () => {
    if (!selectedGame) {
      navigate('/games');
      return;
    }
    
    try {
      const conversation = await createNewConversation(selectedGame.id, selectedGame.name);
      navigate(`/chat/${selectedGame.id}/${conversation.id}`);
    } catch (error) {
      console.error('Failed to create conversation:', error);
      // Fallback to simple chat navigation
      navigate(`/chat/${selectedGame.id}`);
    }
  };

  const actions = [
    {
      title: selectedGame ? `Chat about ${selectedGame.name}` : 'Select a Game First',
      description: selectedGame 
        ? `Ask questions about ${selectedGame.name} rules and gameplay`
        : 'Choose a game from the library to start asking questions',
      icon: '💬',
      color: 'blue',
      action: handleStartChat,
      disabled: false,
    },
    {
      title: 'Browse Game Library',
      description: 'Explore available games and select one to focus on',
      icon: '📚',
      color: 'green',
      action: () => navigate('/games'),
      disabled: false,
    },
    {
      title: 'View Conversations',
      description: 'Browse your chat history and continue previous discussions',
      icon: '📋',
      color: 'purple',
      action: () => navigate('/conversations'),
      disabled: false,
    },
    {
      title: 'Quick Rule Search',
      description: 'Jump straight to chat for fast rule lookups',
      icon: '🔍',
      color: 'yellow',
      action: () => navigate('/chat'),
      disabled: false,
    },
  ];

  const getColorClasses = (color: string, disabled: boolean) => {
    if (disabled) {
      return {
        bg: 'bg-gray-100',
        icon: 'bg-gray-200 text-gray-400',
        button: 'bg-gray-100 text-gray-400 cursor-not-allowed',
      };
    }
    
    const colorMap: Record<string, any> = {
      blue: {
        bg: 'bg-blue-50 hover:bg-blue-100',
        icon: 'bg-blue-100 text-blue-600',
        button: 'bg-blue-600 hover:bg-blue-700 text-white',
      },
      green: {
        bg: 'bg-green-50 hover:bg-green-100',
        icon: 'bg-green-100 text-green-600',
        button: 'bg-green-600 hover:bg-green-700 text-white',
      },
      purple: {
        bg: 'bg-purple-50 hover:bg-purple-100',
        icon: 'bg-purple-100 text-purple-600',
        button: 'bg-purple-600 hover:bg-purple-700 text-white',
      },
      yellow: {
        bg: 'bg-yellow-50 hover:bg-yellow-100',
        icon: 'bg-yellow-100 text-yellow-600',
        button: 'bg-yellow-600 hover:bg-yellow-700 text-white',
      },
    };
    
    return colorMap[color] || colorMap.blue;
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-6">
        Quick Actions
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {actions.map((action, index) => {
          const colors = getColorClasses(action.color, action.disabled);
          
          return (
            <div
              key={index}
              className={`p-4 rounded-lg border transition-colors ${colors.bg} ${
                action.disabled ? 'cursor-not-allowed' : 'cursor-pointer'
              }`}
              onClick={!action.disabled ? action.action : undefined}
            >
              <div className="flex items-start space-x-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${colors.icon}`}>
                  <span className="text-lg">{action.icon}</span>
                </div>
                
                <div className="flex-1 min-w-0">
                  <h4 className={`text-sm font-medium mb-1 ${
                    action.disabled ? 'text-gray-400' : 'text-gray-900'
                  }`}>
                    {action.title}
                  </h4>
                  <p className={`text-xs ${
                    action.disabled ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {action.description}
                  </p>
                </div>
              </div>
              
              <div className="mt-3">
                <button
                  onClick={!action.disabled ? action.action : undefined}
                  disabled={action.disabled}
                  className={`w-full px-3 py-2 text-xs font-medium rounded-md transition-colors ${colors.button}`}
                >
                  {action.disabled ? 'Select Game First' : 'Go'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};